import { useSnackbar } from "@livingdesign/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { getService, getServiceWithResponseType } from "../../../axios/axios";
import { Operations } from "../../../axios/operations";
import { USER_REQUEST_SERVICE } from "../../../constants/baseURLs";
import {
  EXPORT_MAX_LIMIT_CONF, SHOW_NO_RESULT, USER_REQUESTS
} from "../../../constants/constants";
import {
  ENABLE_SERVER_SIDE_FEATURES_USER_REQUESTS,
  ROWS_PER_PAGE_OPTIONS_USER_REQUESTS,
  ROWS_PER_PAGE_USER_REQUESTS
} from "../../../constants/tableConfigs";
import { exportClientData, formSearchQuery, getExportedData } from "../../../helpers/tableUtils";
import { getErrorMessage } from "../../../helpers/utils";
import { setUserRequestData } from "../../../store/actions/adminUserRequestActions";
import { COLUMNS_PEOPLE, FILTER_CONFIGS_PEOPLE } from "./OrganizationPeople";

/**
 * Shared hook for Pending and Rejected user-request tables.
 * Only the status filter value, the row-click path, and the selectedPeopleTab differ.
 *
 * @param {object} options
 * @param {'pending'|'rejected'} options.statusFilter   - CS filter value for the status field
 * @param {string}              options.requestPath     - URL segment for row-click navigation
 * @param {number}              options.selectedPeopleTab - Tab index passed to the detail page
 */
const useUserRequestsTable = ({ statusFilter, requestPath, selectedPeopleTab }) => {
  const { addSnack } = useSnackbar();
  const dispatch = useDispatch();
  const history = useHistory();
  const { userRequestData } = useSelector((state) => state?.adminUserRequest);
  const identifier = useSelector((state) => state?.organization?.organizationDetailValues?.identifier);

  const [userColumns, setUserColumns] = useState(COLUMNS_PEOPLE);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [sortQuery, setSortQuery] = useState("&order=createdTs desc");
  const [exportLoading, setExportLoading] = useState(false);
  const [createExportLoading, setCreateExportLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectRowsData, setSelectRowsData] = useState([]);
  const [rowsData, setRowsData] = useState([]);
  const [currentPageExport, setCurrentPageExport] = useState(false);
  const [rowsPerPageUserRequests, setRowsPerPageUserRequests] = useState(ROWS_PER_PAGE_USER_REQUESTS);

  const showExportAllOption = userRequestData?.count < EXPORT_MAX_LIMIT_CONF.USER_REQUESTS;
  const defaultExportOption = showExportAllOption ? 'all' + USER_REQUESTS : 'currentPage';
  const [exportSelection, setExportSelection] = useState(defaultExportOption);

  const defaultFilterCondition = `${formSearchQuery(
    "isActive", Operations.EQ, true, 'boolean'
  )}+${formSearchQuery(
    "status", Operations.LIKEIC, statusFilter, 'id'
  )}+${formSearchQuery(
    "organizationId", Operations.EQ, identifier, 'id'
  )}`;

  const getUserRequestData = (query = "") => {
    setLoading(true);
    getService(
      USER_REQUEST_SERVICE,
      `/search/user-requests/v1?filter=(${defaultFilterCondition})${searchQuery !== '' ?
        'and (' : ''}${searchQuery}${searchQuery !== '' ? ')' : ''}${filterQuery !== '' ?
          'and (' : ''}${filterQuery}${filterQuery !== '' ? ')' : ''}${sortQuery} ${query}`
    )
      .then((response) => {
        dispatch(setUserRequestData(response?.data));
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        addSnack({ message: getErrorMessage(error) });
      });
  };

  const handleServerSideSearch = (searchText) => {
    const query = searchText ? getSearchQuery(searchText, COLUMNS_PEOPLE) : "";
    if (query !== SHOW_NO_RESULT) {
      setSearchQuery(query);
    } else {
      dispatch(setUserRequestData([]));
    }
  };

  const handleServerSideFilter = (query) => setFilterQuery(query);

  const handleServerSidePagination = ({ rowsPerPage, page }) => {
    setRowsPerPageUserRequests(rowsPerPage);
    getUserRequestData(`&limit=${rowsPerPage}&offset=${rowsPerPage * page}`);
  };

  const handleServerSideSort = ({ name, direction }) => {
    setSortQuery(`&order=${name} ${direction}`);
  };

  const userAccessCustomOptions = {
    rowsPerPage: rowsPerPageUserRequests,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS_USER_REQUESTS,
    filterConfigs: FILTER_CONFIGS_PEOPLE,
    serverSide: ENABLE_SERVER_SIDE_FEATURES_USER_REQUESTS,
    onServerSideSearch: handleServerSideSearch,
    onServerSideFilter: handleServerSideFilter,
    onServerSidePagination: handleServerSidePagination,
    onServerSideSort: handleServerSideSort,
    totalCount: userRequestData?.count,
  };

  const onCancelBtnClk = () => {
    setExportLoading(false);
    setModalOpen(false);
    setExportSelection(defaultExportOption);
  };

  const handleExportBtnClk = () => {
    setExportSelection(defaultExportOption);
    setModalOpen(true);
    setExportLoading(true);
    setCurrentPageExport(true);
  };

  const handleRowSelectionChange = (currentSelect, allSelected, selectRowsData) => {
    const bData = userRequestData?.userRequests;
    const result = allSelected?.map((item) => bData && bData[item?.index]);
    setSelectRowsData(selectRowsData);
    setRowsData(result);
  };

  const handleRowClick = (rowData, rowMeta) => {
    const id = userRequestData?.userRequests[rowMeta?.dataIndex]?.id;
    history.push(`/people/${requestPath}${id}/view`, { selectedPeopleTab });
  };

  const resetSelectedRows = () => {
    setSelectRowsData([]);
    setRowsData([]);
  };

  const onClickCreate = () => {
    setCreateExportLoading(true);
    if (ENABLE_SERVER_SIDE_FEATURES_USER_REQUESTS === false) {
      exportClientData(userRequestData?.userRequests, exportSelection, rowsData, 'user-requests');
      setModalOpen(false);
      setExportLoading(false);
      setCreateExportLoading(false);
    } else {
      let exportQuery = `filter=${defaultFilterCondition} ${searchQuery} ${filterQuery} ${sortQuery}&limit=${rowsPerPageUserRequests}&offset=0`;
      if (exportSelection === 'currentSelection') {
        const result = rowsData?.map((a) => a?.id);
        const innerQuery = formSearchQuery('id', Operations.IN, result, 'array') + '+' + formSearchQuery('organizationId', Operations.EQ, identifier, 'id');
        exportQuery = `filter=${encodeURIComponent(innerQuery)}`;
      } else if (exportSelection === 'currentPage') {
        const result = userRequestData?.userRequests?.map((a) => a?.id);
        const innerQuery = formSearchQuery('id', Operations.IN, result, 'array') + '+' + formSearchQuery('organizationId', Operations.EQ, identifier, 'id');
        exportQuery = `filter=${encodeURIComponent(innerQuery)}`;
      }
      getServiceWithResponseType(
        USER_REQUEST_SERVICE,
        `/search/user-requests/v1?csv=true&${exportQuery}`,
        "arraybuffer"
      )
        .then((response) => {
          getExportedData(response?.data, response?.headers["content-type"], 'user-requests');
          setModalOpen(false);
          setExportLoading(false);
          setCreateExportLoading(false);
        })
        .catch((error) => {
          setModalOpen(false);
          setExportLoading(false);
          setCreateExportLoading(false);
          addSnack({ message: getErrorMessage(error) });
        });
    }
  };

  useEffect(() => {
    const offsetQuery = ROWS_PER_PAGE_OPTIONS_USER_REQUESTS
      ? `&limit=${rowsPerPageUserRequests}&offset=0`
      : "";
    getUserRequestData(offsetQuery);
  }, [searchQuery, filterQuery, sortQuery]);

  return {
    userColumns,
    setUserColumns,
    userRequestData,
    loading,
    userAccessCustomOptions,
    exportLoading,
    createExportLoading,
    modalOpen,
    selectRowsData,
    rowsData,
    currentPageExport,
    setCurrentPageExport,
    exportSelection,
    setExportSelection,
    showExportAllOption,
    onCancelBtnClk,
    handleExportBtnClk,
    handleRowSelectionChange,
    handleRowClick,
    resetSelectedRows,
    onClickCreate,
  };
};

/**
 * Form search query from user-entered search text across visible columns.
 */
export const getSearchQuery = (searchText, COLUMNS) => {
  let query = "";
  COLUMNS.forEach((column, index) => {
    if (column?.options?.display) {
      const colQuery = formSearchQuery(column?.name, column?.operation, searchText, column?.type);
      if (colQuery !== "") {
        query += index > 0 ? " OR " + colQuery : colQuery;
      }
    }
  });
  return query;
};

export default useUserRequestsTable;
